import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowCounterClockwise, Trash, Lightning, ArrowsClockwise, Clock, Warning, CheckCircle, XCircle, HourglassHigh, Flask, CaretDown, CaretUp } from "@phosphor-icons/react";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: "Beklemede", color: "bg-amber-100 text-amber-700 border-amber-200", icon: HourglassHigh },
  retrying: { label: "Tekrar Deneniyor", color: "bg-blue-100 text-blue-700 border-blue-200", icon: ArrowsClockwise },
  failed: { label: "Basarisiz", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  resolved: { label: "Cozuldu", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  cancelled: { label: "Iptal Edildi", color: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
};

const ACTION_LABELS = {
  cutoff: "Elektrik Kesme",
  reopen: "Sayac Acma",
  turn_on: "Cihaz Acma",
  turn_off: "Cihaz Kapatma",
};

const SOURCE_LABELS = {
  overdue: "Fatura Gecikme",
  payment: "Odeme",
  auto_on: "Otomatik Acma",
  manual: "Manuel",
  bypass: "Bypass",
  test: "Test Simulasyonu",
  debt_cutoff: "Borc Kesimi",
  debt_reopen: "Borc Odeme Acma",
};

const FAIL_MODE_LABELS = {
  always_fail: "Her zaman basarisiz",
  fail_once: "Bir kez basarisiz, sonra basarili",
  resolve_on_retry: "Ilk retry'da cozulur",
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const timeUntil = (iso) => {
  if (!iso) return "-";
  const diff = new Date(iso) - new Date();
  if (diff <= 0) return "Simdi";
  const min = Math.floor(diff / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  return min > 0 ? `${min}dk ${sec}sn` : `${sec}sn`;
};

function SimulationPanel({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [actionType, setActionType] = useState("turn_off");
  const [failMode, setFailMode] = useState("always_fail");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/queue/simulate-failure`, {
        action_type: actionType,
        fail_mode: failMode,
      });
      toast.success(res.data.message);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulasyon olusturulamadi");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border border-dashed border-violet-300 rounded-xl bg-violet-50/40" data-testid="simulation-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-violet-700 hover:bg-violet-50 rounded-xl transition-colors"
        data-testid="simulation-toggle"
      >
        <span className="flex items-center gap-2">
          <Flask size={14} weight="bold" />
          Test Araci — Simulasyon Queue Kaydi Olustur
        </span>
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-[10px] font-medium text-violet-600">Islem Tipi</label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger className="h-8 w-36 text-xs bg-white" data-testid="sim-action-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="turn_off">Cihaz Kapatma</SelectItem>
                <SelectItem value="turn_on">Cihaz Acma</SelectItem>
                <SelectItem value="cutoff">Elektrik Kesme</SelectItem>
                <SelectItem value="reopen">Sayac Acma</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-medium text-violet-600">Basarisizlik Modu</label>
            <Select value={failMode} onValueChange={setFailMode}>
              <SelectTrigger className="h-8 w-56 text-xs bg-white" data-testid="sim-fail-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="always_fail">Her zaman basarisiz</SelectItem>
                <SelectItem value="fail_once">Bir kez basarisiz, sonra basarili</SelectItem>
                <SelectItem value="resolve_on_retry">Ilk retry'da cozulur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            className="h-8 bg-violet-600 hover:bg-violet-700 text-xs"
            onClick={handleCreate}
            disabled={creating}
            data-testid="sim-create-btn"
          >
            <Flask size={12} className="mr-1" />
            {creating ? "Olusturuluyor..." : "Olustur"}
          </Button>
          <p className="text-[10px] text-violet-400 w-full mt-1">
            Retry worker 60sn aralikla calisir. Olusturulan test kaydi ilk retry'da yukaridaki moda gore davranir.
          </p>
        </div>
      )}
    </div>
  );
}

export default function QueuePage() {
  const [stats, setStats] = useState({ pending: 0, retrying: 0, failed: 0, resolved: 0, cancelled: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/queue-stats`);
      setStats(res.data.stats || {});
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = activeTab === "all" ? "" : activeTab;
      const params = new URLSearchParams({ page, limit: pageSize });
      if (statusFilter) params.append("status", statusFilter);
      const res = await axios.get(`${API_URL}/api/admin/queue/list?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Kuyruk listesi yuklenemedi");
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, pageSize]);

  useEffect(() => { fetchStats(); fetchList(); }, [fetchStats, fetchList]);

  // Auto-refresh her 12sn
  useEffect(() => {
    const interval = setInterval(() => { fetchStats(); fetchList(); }, 12000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchList]);

  const handleRetrySingle = async (id, name) => {
    try {
      await axios.post(`${API_URL}/api/admin/queue/${id}/retry`);
      toast.success(`${name}: Tekrar deneme kuyurguna alindi`);
      fetchStats(); fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Islem basarisiz");
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await axios.delete(`${API_URL}/api/admin/queue/${id}`);
      toast.success(`${name}: Kayit silindi`);
      fetchStats(); fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silme basarisiz");
    }
  };

  const handleRetryAll = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/queue/retry-failed`);
      toast.success(res.data.message);
      fetchStats(); fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu retry basarisiz");
    }
  };

  const handleCleanup = async () => {
    try {
      const res = await axios.delete(`${API_URL}/api/admin/queue/cleanup`);
      toast.success(res.data.message);
      fetchStats(); fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Temizleme basarisiz");
    }
  };

  const tabs = [
    { key: "pending", label: "Bekleyen", count: stats.pending },
    { key: "retrying", label: "Deneniyor", count: stats.retrying },
    { key: "failed", label: "Basarisiz", count: stats.failed },
    { key: "resolved", label: "Cozulmus", count: stats.resolved },
    { key: "cancelled", label: "Iptal", count: stats.cancelled },
  ];

  return (
    <div className="space-y-6" data-testid="queue-management-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {tabs.map(t => {
          const cfg = STATUS_CONFIG[t.key];
          const Icon = cfg.icon;
          return (
            <Card key={t.key} className={`cursor-pointer transition-all duration-200 border-2 ${activeTab === t.key ? "border-indigo-400 shadow-md" : "border-transparent hover:border-slate-200"}`}
              onClick={() => { setActiveTab(t.key); setPage(1); }}
              data-testid={`queue-stat-${t.key}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${cfg.color}`}><Icon size={20} weight="bold" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{t.count || 0}</p>
                  <p className="text-xs text-slate-500">{t.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchList(); }} data-testid="queue-refresh-btn">
          <ArrowsClockwise size={14} className="mr-1.5" /> Yenile
        </Button>
        {stats.failed > 0 && (
          <Button variant="outline" size="sm" onClick={handleRetryAll} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="queue-retry-all-btn">
            <ArrowCounterClockwise size={14} className="mr-1.5" /> Tum Basarisizlari Tekrar Dene ({stats.failed})
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={handleCleanup} className="border-slate-300 text-slate-600" data-testid="queue-cleanup-btn">
          <Trash size={14} className="mr-1.5" /> 30 Gunluk Temizlik
        </Button>
        <span className="text-xs text-slate-400 ml-auto">Otomatik yenileme: 12sn</span>
      </div>

      {/* Test Simulation Panel */}
      <SimulationPanel onCreated={() => { fetchStats(); fetchList(); }} />

      {/* Table */}
      <Card>
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Lightning size={16} weight="bold" />
            {tabs.find(t => t.key === activeTab)?.label || "Tumu"} ({total})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Yukleniyor...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm" data-testid="queue-empty">
              Bu kategoride kayit bulunmuyor.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/70">
                    <TableHead className="text-[11px] py-2 pl-5">Cihaz</TableHead>
                    <TableHead className="text-[11px] py-2">Islem</TableHead>
                    <TableHead className="text-[11px] py-2">Kaynak</TableHead>
                    <TableHead className="text-[11px] py-2">Durum</TableHead>
                    <TableHead className="text-[11px] py-2 text-center">Retry</TableHead>
                    <TableHead className="text-[11px] py-2">Son Hata</TableHead>
                    <TableHead className="text-[11px] py-2">Sonraki Deneme</TableHead>
                    <TableHead className="text-[11px] py-2">Olusturulma</TableHead>
                    <TableHead className="text-[11px] py-2 text-right pr-5">Islem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => {
                    const sCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                    const SIcon = sCfg.icon;
                    return (
                      <TableRow key={item.id} className="hover:bg-slate-50/50" data-testid={`queue-row-${item.id}`}>
                        <TableCell className="py-2.5 pl-5">
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-medium text-slate-800 truncate max-w-[140px]">{item.device_name || item.device_id?.slice(0, 12)}</p>
                              {item.is_test && <Badge className="text-[9px] bg-violet-100 text-violet-700 border border-violet-200 px-1 py-0">TEST</Badge>}
                            </div>
                            {item.is_test && item.fail_mode && <p className="text-[10px] text-violet-400">{FAIL_MODE_LABELS[item.fail_mode]}</p>}
                            {item.tenant_id && <p className="text-[10px] text-slate-400 truncate max-w-[160px]">Kiraci: {item.tenant_id.slice(0, 12)}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <Badge className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200">{ACTION_LABELS[item.action_type] || item.action_type}</Badge>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-[10px] text-slate-500">{SOURCE_LABELS[item.source] || item.source}</span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <Badge className={`text-[10px] border ${sCfg.color} flex items-center gap-1 w-fit`}>
                            <SIcon size={11} weight="bold" />{sCfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2.5 text-center">
                          <span className={`text-xs font-mono ${item.retry_count >= item.max_retries ? "text-red-600 font-bold" : "text-slate-600"}`}>
                            {item.retry_count}/{item.max_retries}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-[10px] text-slate-500 truncate block max-w-[150px]" title={item.last_error || "-"}>{item.last_error || "-"}</span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          {item.status === "pending" || item.status === "retrying" ? (
                            <span className="text-[10px] text-blue-600 flex items-center gap-1">
                              <Clock size={11} />{timeUntil(item.next_retry_at)}
                            </span>
                          ) : <span className="text-[10px] text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="py-2.5">
                          <span className="text-[10px] text-slate-500">{formatDate(item.created_at)}</span>
                        </TableCell>
                        <TableCell className="py-2.5 text-right pr-5">
                          <div className="flex items-center gap-1.5 justify-end">
                            {item.status !== "resolved" && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-blue-600 hover:bg-blue-50"
                                onClick={() => handleRetrySingle(item.id, item.device_name)} data-testid={`queue-retry-${item.id}`}>
                                <ArrowCounterClockwise size={12} className="mr-1" />Dene
                              </Button>
                            )}
                            {(item.status === "resolved" || item.status === "failed") && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-red-500 hover:bg-red-50"
                                onClick={() => handleDelete(item.id, item.device_name)} data-testid={`queue-delete-${item.id}`}>
                                <Trash size={12} className="mr-1" />Sil
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {total > pageSize && (
            <div className="px-5 py-3 border-t">
              <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
